import { BASE_URL } from '../../../conf';
import previewCalibration from '../../../data/studioPreviewCalibration.json';

export const STUDIO_PLATE_SIZE = [1200, 1500];

export const COLOR_SLUG = {
  White: 'white',
  Pink: 'pink',
  'Baby Blue': 'baby-blue',
  Stone: 'stone',
  Grey: 'grey',
  Black: 'black',
};

const PRODUCT_KEY = {
  tshirt: 'tshirt',
  hoody: 'hoody',
};

// ponytail: normalized print boxes for views without calibration entries; upgrade path is full calibration JSON
const FALLBACK_PRINT_BOX = {
  tshirt: {
    'hanging-2': [0.35, 0.34, 0.65, 0.62],
    folded: [0.33, 0.30, 0.67, 0.61],
  },
  hoody: {
    'hanging-2': [0.35, 0.34, 0.65, 0.58],
    folded: [0.33, 0.39, 0.67, 0.66],
  },
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const getStudioPlateUrl = (productType, plateView, colorName) => {
  const product = PRODUCT_KEY[productType] || 'tshirt';
  const colour = COLOR_SLUG[colorName] || 'white';
  if (import.meta.env.DEV) {
    return `/studio-plates/${product}/${plateView}-${colour}.png`;
  }
  return `${BASE_URL}/api/studio/mockup-plate/${product}/${plateView}/${colour}/`;
};

export const getStudioOcclusionMaskUrl = (productType, plateView) => {
  const product = PRODUCT_KEY[productType] || 'tshirt';
  if (product !== 'hoody' || plateView !== 'front') return '';
  return `${BASE_URL}/api/studio/mockup-occlusion-mask/${product}/${plateView}/`;
};

const getCalibrationEntry = (productType, colorName, calibrationId) => {
  if (!calibrationId) return null;
  const product = PRODUCT_KEY[productType] || 'tshirt';
  const colour = COLOR_SLUG[colorName] || 'white';
  return previewCalibration?.previews?.[calibrationId]?.products?.[product]?.colours?.[colour] || null;
};

export const getCalibratedPrintBox = (productType, colorName, mockupOption, plateSize = STUDIO_PLATE_SIZE) => {
  const [plateWidth, plateHeight] = plateSize;
  const calibration = getCalibrationEntry(productType, colorName, mockupOption.calibrationId);
  if (calibration?.printBoxPx) {
    const [x1, y1, x2, y2] = calibration.printBoxPx;
    const sourceWidth = calibration.plateSize?.[0] || plateWidth;
    const sourceHeight = calibration.plateSize?.[1] || plateHeight;
    const scaleX = plateWidth / sourceWidth;
    const scaleY = plateHeight / sourceHeight;
    return [
      Math.round(x1 * scaleX),
      Math.round(y1 * scaleY),
      Math.round(x2 * scaleX),
      Math.round(y2 * scaleY),
    ];
  }

  const product = PRODUCT_KEY[productType] || 'tshirt';
  const normalized = FALLBACK_PRINT_BOX[product]?.[mockupOption.plateView];
  if (!normalized) return [0, 0, plateWidth, plateHeight];
  const [x1, y1, x2, y2] = normalized;
  return [
    Math.round(x1 * plateWidth),
    Math.round(y1 * plateHeight),
    Math.round(x2 * plateWidth),
    Math.round(y2 * plateHeight),
  ];
};

export const getPreviewPrintBox = (productType, colorName, mockupOption, sourcePrintRect, sourceLayout, plateSize = STUDIO_PLATE_SIZE) => {
  const [plateWidth, plateHeight] = plateSize;
  const calibration = getCalibrationEntry(productType, colorName, mockupOption.calibrationId);
  const calibratedBox = getCalibratedPrintBox(productType, colorName, mockupOption, plateSize);
  const garmentBounds = calibration?.garmentBoundsPx;

  if (garmentBounds && sourceLayout?.width && sourceLayout?.height && sourcePrintRect?.width && sourcePrintRect?.height) {
    const product = PRODUCT_KEY[productType] || 'tshirt';
    const [garmentLeft, garmentTop, garmentRight, garmentBottom] = garmentBounds;
    const garmentWidth = Math.max(1, garmentRight - garmentLeft);
    const garmentHeight = Math.max(1, garmentBottom - garmentTop);
    const left = garmentLeft + (sourcePrintRect.left / sourceLayout.width) * garmentWidth;
    const projectedTop = garmentTop + (sourcePrintRect.top / sourceLayout.height) * garmentHeight;
    const calibratedTop = calibratedBox[1];
    const shouldUseCalibratedTopAnchor = product === 'tshirt'
      && (mockupOption.plateView === 'front' || mockupOption.plateView === 'back');
    const top = shouldUseCalibratedTopAnchor ? calibratedTop : projectedTop;
    const width = (sourcePrintRect.width / sourceLayout.width) * garmentWidth;
    const height = width / (sourcePrintRect.width / sourcePrintRect.height);
    return [
      clamp(left, 0, plateWidth),
      clamp(top, 0, plateHeight),
      clamp(left + width, 0, plateWidth),
      clamp(top + height, 0, plateHeight),
    ];
  }

  return calibratedBox;
};

export const artworkRectFromStudioTransform = (imageSize, box, transform, defaultScale = 1) => {
  const [imageWidth, imageHeight] = imageSize;
  const [x1, y1, x2, y2] = box;
  const boxWidth = Math.max(1, x2 - x1);
  const boxHeight = Math.max(1, y2 - y1);
  if (imageWidth <= 0 || imageHeight <= 0) return null;

  const offsetX = clamp(toNumber(transform?.offsetX), -1, 1);
  const offsetY = clamp(toNumber(transform?.offsetY), -1, 1);
  const placementMode = transform?.mode === 'expanded' ? 'expanded' : 'standard';
  const scaleMax = placementMode === 'expanded' ? 2.5 : 1;
  const scale = clamp(toNumber(transform?.scale, defaultScale), 0.16, scaleMax);

  const targetSize = Math.max(1, Math.round(Math.min(boxWidth, boxHeight) * scale));
  const minCenterX = x1 + targetSize / 2;
  const maxCenterX = x2 - targetSize / 2;
  const minCenterY = y1 + targetSize / 2;
  const maxCenterY = y2 - targetSize / 2;

  const rawCenterX = x1 + boxWidth / 2 + offsetX * (boxWidth / 2);
  const rawCenterY = y1 + boxHeight / 2 + offsetY * (boxHeight / 2);
  const centerX = placementMode === 'expanded'
    ? clamp(rawCenterX, x1, x2)
    : (minCenterX <= maxCenterX ? clamp(rawCenterX, minCenterX, maxCenterX) : x1 + boxWidth / 2);
  const centerY = placementMode === 'expanded'
    ? clamp(rawCenterY, y1, y2)
    : (minCenterY <= maxCenterY ? clamp(rawCenterY, minCenterY, maxCenterY) : y1 + boxHeight / 2);

  const left = Math.round(centerX - targetSize / 2);
  const top = Math.round(centerY - targetSize / 2);
  return [left, top, left + targetSize, top + targetSize];
};

export const getPreviewArtworkStyle = (productType, colorName, mockupOption, transform, plateSize = STUDIO_PLATE_SIZE) => {
  const [plateWidth, plateHeight] = plateSize;
  const box = getCalibratedPrintBox(productType, colorName, mockupOption, plateSize);
  const rect = artworkRectFromStudioTransform(plateSize, box, transform, 1);
  if (!rect) return null;
  const [left, top, right, bottom] = rect;
  return {
    left: `${(left / plateWidth) * 100}%`,
    top: `${(top / plateHeight) * 100}%`,
    width: `${((right - left) / plateWidth) * 100}%`,
    height: `${((bottom - top) / plateHeight) * 100}%`,
  };
};

export const getPreviewPrintBoxStyle = (productType, colorName, mockupOption, sourcePrintRect, sourceLayout, plateSize = STUDIO_PLATE_SIZE) => {
  const [plateWidth, plateHeight] = plateSize;
  const [left, top, right, bottom] = getPreviewPrintBox(productType, colorName, mockupOption, sourcePrintRect, sourceLayout, plateSize);
  return {
    left: `${(left / plateWidth) * 100}%`,
    top: `${(top / plateHeight) * 100}%`,
    width: `${((right - left) / plateWidth) * 100}%`,
    height: `${((bottom - top) / plateHeight) * 100}%`,
  };
};

export const getPreviewLayerStyle = (layer, zIndex, printRect) => {
  const scaleX = Math.max(0.05, layer.scaleX ?? layer.scale ?? 1);
  const width = scaleX * 100;
  const aspectRatio = Number.isFinite(layer.aspectRatio) && layer.aspectRatio > 0
    ? layer.aspectRatio
    : null;
  const printAspectRatio = printRect?.width && printRect?.height
    ? printRect.width / printRect.height
    : 1;
  const height = aspectRatio
    ? (width * printAspectRatio) / aspectRatio
    : Math.max(0.05, layer.scaleY ?? layer.scale ?? 1) * 100;
  return {
    left: `${50 + (layer.x || 0) * 50}%`,
    top: `${50 + (layer.y || 0) * 50}%`,
    width: `${width}%`,
    height: `${height}%`,
    zIndex,
  };
};

if (import.meta.env.DEV) {
  const sampleBox = getCalibratedPrintBox('tshirt', 'White', { calibrationId: 'front-2', plateView: 'front' });
  console.assert(
    sampleBox.join(',') === '408,420,792,870',
    'studio preview calibration drifted',
    sampleBox
  );
}
